import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import Cookies from "universal-cookie";
import toast from "react-hot-toast";
import useGet from "../read/useGet";
import usePut from "../put/usePut";
import { COMPANY_ID, COMPANY_EMPLOYEE_ID } from "../../config";

const cookies = new Cookies();

export const useEmployeePettyCashLedger = ({ onDeleteSuccess }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = cookies.get(COMPANY_ID);
  const userEmployeeId = cookies.get(COMPANY_EMPLOYEE_ID);

  const initialTxnFilters = {
    limit: 10,
    page_number: parseInt(searchParams.get("page_number")) || 0,
    search_query: searchParams.get("search_query") || "",
    from_date: searchParams.get("from_date") || "",
    to_date: searchParams.get("to_date") || "",
    approval_status: searchParams.get("approval_status") || "",
  };

  const [searchQuery, setSearchQuery] = useState(initialTxnFilters.search_query);
  const [selectedTxnId, setSelectedTxnId] = useState(null);

  // 1. Fetch Transaction Listing
  const {
    list: transactionListing,
    paramsObject: txnFilters,
    setGetListParams: setTxnFilters,
  } = useGet({
    url: "petty-cash/transaction_listing",
    initialData: initialTxnFilters,
    enabled: true,
  });

  // 2. Fetch Petty Cash Statistics for the logged-in employee
  const { list: statisticsListing } = useGet({
    url: "petty-cash/statistics",
    enabled: true,
  });

  const txnFiltersRef = useRef(txnFilters);
  txnFiltersRef.current = txnFilters;

  // 3. Delete Transaction Mutation
  const { handleAuth: deleteTransactionMutation } = usePut({
    url: "petty-cash/delete_transaction",
    refreshUrl: ["petty-cash/transaction_listing", "petty-cash/statistics"],
    onSuccess: () => {
      toast.success("Transaction Deleted Successfully");
      onDeleteSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete transaction");
    },
  });

  const { mutate: _deleteTransaction, isLoading: isDeleting } = deleteTransactionMutation;

  const deleteTransaction = (transactionId) => {
    _deleteTransaction({
      transaction_id: transactionId,
      company_employee_id: userEmployeeId,
    });
  };

  // 4. Export Report Mutation
  const { handleAuth: exportReportMutation } = usePut({
    url: "petty-cash/transaction_report",
    axiosConfig: { responseType: "blob" },
    onSuccess: (res) => {
      const href = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = href;
      a.download = `Petty_Cash_Transaction_Report.xlsx`;
      a.click();
      toast.success("Report downloaded successfully");
    },
    onError: async (err) => {
      let message = "Failed to export report";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch { }
      }
      toast.error(message);
    },
  });

  const { mutate: _exportReport, isLoading: isExporting } = exportReportMutation;

  const exportReport = () => {
    _exportReport({
      ...(companyId && { company_id: companyId }),
      ...(userEmployeeId && { company_employee_id: userEmployeeId }),
      ...(txnFilters.from_date && { from_date: txnFilters.from_date }),
      ...(txnFilters.to_date && { to_date: txnFilters.to_date }),
      ...(txnFilters.approval_status && { approval_status: txnFilters.approval_status }),
    });
  };

  // 5. Fetch Single Transaction Details
  const {
    list: activeTxnQuery,
    setGetListParams: setActiveTxnParams,
  } = useGet({
    url: "petty-cash/fetch_single_transaction",
    initialData: { transaction_id: selectedTxnId },
    enabled: !!selectedTxnId,
  });

  const handleViewDetails = (txnId) => {
    setSelectedTxnId(txnId);
    setActiveTxnParams({ transaction_id: txnId });
  };

  const handlePageClick = (val) => {
    const updated = { ...txnFilters, page_number: val.selected };
    setTxnFilters(updated);
    setSearchParams(updated);
  };

  const handleTxnFilterChange = (field, value) => {
    const updated = { ...txnFilters, [field]: value, page_number: 0 };
    setTxnFilters(updated);
    setSearchParams(updated);
  };

  const debouncedSearch = useRef(
    debounce((value) => {
      const updated = { ...txnFiltersRef.current, search_query: value, page_number: 0 };
      setTxnFilters(updated);
      setSearchParams(updated);
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  return {
    transactionListing,
    statisticsListing,
    activeTxnQuery,
    txnFilters,
    searchQuery,
    selectedTxnId,
    setSelectedTxnId,
    isDeleting,
    isExporting,
    deleteTransaction,
    exportReport,
    handleViewDetails,
    handlePageClick,
    handleTxnFilterChange,
    handleSearchChange,
  };
};
